import { useState, useEffect, type FormEvent } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "wouter";
import { ArrowLeft, Plus, Mail, Shield, UserCircle, Trash2, KeyRound, AlertTriangle } from "lucide-react";
import InfoTip from "@/components/info-tip";
import { useToast } from "@/hooks/use-toast";

const BASE = import.meta.env.BASE_URL;

function getAdminHeaders(): Record<string, string> {
  const token = localStorage.getItem("admin_token") || "";
  return { "Content-Type": "application/json", "x-admin-token": token };
}

function getCurrentAdminRole(): string {
  try {
    const raw = localStorage.getItem("admin_user");
    if (!raw) return "";
    return JSON.parse(raw)?.role || "";
  } catch { return ""; }
}

const roleStyles: Record<string, string> = {
  master_admin: "bg-primary/10 text-primary",
  admin: "bg-purple-100 text-purple-800",
  operator: "bg-blue-100 text-blue-800",
};

const roleLabels: Record<string, string> = {
  master_admin: "Master Admin",
  admin: "Admin",
  operator: "Operator",
};

interface TeamMember {
  id: number;
  name: string;
  email: string;
  role: string;
  hasPassword?: boolean;
  createdAt: string;
}

export default function AdminTeam() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<TeamMember | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const { toast } = useToast();
  const myRole = getCurrentAdminRole();
  const isMasterAdmin = myRole === "master_admin";

  const fetchMembers = async () => {
    try {
      const res = await fetch(`${BASE}api/team-members`, { headers: getAdminHeaders() });
      if (res.ok) {
        setMembers(await res.json());
      }
    } catch {}
    setIsLoading(false);
  };

  useEffect(() => { fetchMembers(); }, []);

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);

    try {
      const res = await fetch(`${BASE}api/team-members`, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify({
          name: form.get("name"),
          email: form.get("email"),
          password: form.get("password"),
          role: form.get("role") || "operator",
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to add team member");
      }
      toast({ title: "Team member added" });
      setDialogOpen(false);
      fetchMembers();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!confirm(`Remove ${name} from the team?`)) return;
    try {
      const res = await fetch(`${BASE}api/team-members/${id}`, {
        method: "DELETE",
        headers: getAdminHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to remove");
      }
      toast({ title: "Team member removed" });
      fetchMembers();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const submitReset = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!resetTarget) return;
    if (resetPassword.length < 8) {
      toast({ title: "Password too short", description: "Must be at least 8 characters", variant: "destructive" });
      return;
    }
    setResetSubmitting(true);
    try {
      const res = await fetch(`${BASE}api/admin/auth/set-password`, {
        method: "POST",
        headers: getAdminHeaders(),
        body: JSON.stringify({ email: resetTarget.email, newPassword: resetPassword }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to set password");
      }
      toast({
        title: "Password set",
        description: `${resetTarget.name} can now log in at /admin/login. Share the password securely.`,
      });
      setResetTarget(null);
      setResetPassword("");
      fetchMembers();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
    setResetSubmitting(false);
  };

  return (
    <div className="space-y-8" data-testid="page-admin-team">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link href="/admin">
            <button className="p-1.5 rounded-md hover:bg-muted transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </button>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-serif font-bold tracking-tight">Team Members</h1>
              <InfoTip content="People who operate cohorts at Favcy. Master Admins can create and manage all cohorts. Operators are assigned to specific cohorts to manage the day-to-day — reviewing updates, managing founders, curating investor intelligence." />
            </div>
            <p className="text-sm text-muted-foreground mt-1">Manage people who operate Favcy cohorts</p>
          </div>
        </div>
        {isMasterAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-add-member"><Plus className="w-4 h-4 mr-2" />Add Member</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif">Add Team Member</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" placeholder="Full name" required />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" placeholder="name@favcy.in" required />
                </div>
                <div>
                  <Label htmlFor="password">Initial password (min 8 chars)</Label>
                  <Input id="password" name="password" type="password" placeholder="Set a login password" minLength={8} required />
                  <p className="text-xs text-muted-foreground mt-1">Share this with the new admin securely. They can change it from their account.</p>
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select name="role" defaultValue="operator">
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operator">Operator</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="master_admin">Master Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full" data-testid="button-submit-add-member">Add Team Member</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!isMasterAdmin && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>You're viewing the team in read-only mode. Only master admins can add, remove, or reset passwords.</span>
        </div>
      )}

      <Dialog open={!!resetTarget} onOpenChange={(open) => { if (!open) { setResetTarget(null); setResetPassword(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">Reset password</DialogTitle>
          </DialogHeader>
          {resetTarget && (
            <form onSubmit={submitReset} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Setting a new password for <span className="font-medium text-foreground">{resetTarget.name}</span> ({resetTarget.email}).
                Their existing password (if any) will be overwritten. Share the new password securely.
              </p>
              <div>
                <Label htmlFor="new-password">New password (min 8 chars)</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  minLength={8}
                  required
                  autoFocus
                  data-testid="input-reset-password"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" className="flex-1" onClick={() => { setResetTarget(null); setResetPassword(""); }}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={resetSubmitting} data-testid="button-confirm-reset">
                  {resetSubmitting ? "Setting…" : "Set password"}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : members && members.length > 0 ? (
        <div className="space-y-3">
          {members.map((member) => (
            <Card key={member.id} data-testid={`team-member-${member.id}`}>
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-lg font-medium flex-shrink-0">
                  {member.name.split(" ").map((n: string) => n[0]).join("")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold">{member.name}</h3>
                    <Badge className={`text-xs ${roleStyles[member.role] || ""}`}>
                      {member.role === "master_admin" ? <Shield className="w-3 h-3 mr-1" /> : <UserCircle className="w-3 h-3 mr-1" />}
                      {roleLabels[member.role] || member.role}
                    </Badge>
                    {member.hasPassword === false && (
                      <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-200" data-testid={`badge-no-password-${member.id}`}>
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        No password — can't log in
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                    <Mail className="w-3 h-3" />
                    {member.email}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mr-2">
                  Joined {new Date(member.createdAt).toLocaleDateString()}
                </p>
                {isMasterAdmin && (
                  <>
                    <button
                      onClick={() => { setResetTarget(member); setResetPassword(""); }}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                      title="Reset password"
                      data-testid={`button-reset-password-${member.id}`}
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(member.id, member.name)}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors"
                      title="Remove member"
                      data-testid={`button-delete-${member.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No team members yet. Add your first team member to get started.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
